import _ from 'lodash'
import PropTypes from 'prop-types'
import * as React from 'react'

import {findClosestNode} from '@base/dom-utils/nodes'
import {
  cloneCreateOrCallNode,
  forwardProps,
  mergeProps,
} from '@base/react-utils'

export function getBoundaryPath(boundaries: Array<string>): string {
  return '/' + boundaries.join('/')
}

export function getBoundariesFromBoundaryPath(
  boundaryPath: string
): Array<string> {
  return boundaryPath.split('/').slice(1)
}

export const BOUNDARY_ATTR = 'data-boundary'

export function boundariesSelector(boundaries: Array<string>) {
  const path = getBoundaryPath(boundaries)
  return `[${BOUNDARY_ATTR}="${path}"],[${BOUNDARY_ATTR}^="${path}/"]`
}
export function boundarySelector(boundaries: Array<string>) {
  const path = getBoundaryPath(boundaries)
  return `[${BOUNDARY_ATTR}="${path}"]`
}
export function childBoundariesSelector(boundaries: Array<string>) {
  const path = getBoundaryPath(boundaries)
  return `[${BOUNDARY_ATTR}^="${path}/"]`
}

export function getNodeBoundaryPath(node: Element): string {
  const boundaryNode: any = findClosestNode(
    node,
    (n: any) => n.dataset && n.dataset.boundary
  )
  return (
    (boundaryNode && boundaryNode.dataset && boundaryNode.dataset.boundary) ||
    '/'
  )
}

type PathOrElement = string | Array<string> | Element | null | void

export function resolveBoundaryPath(boundaryOrElement: PathOrElement): string {
  if (!boundaryOrElement) {
    return '/'
  } else if (boundaryOrElement instanceof Element) {
    return getNodeBoundaryPath(boundaryOrElement)
  } else if (typeof boundaryOrElement === 'string') {
    return boundaryOrElement
  }
  return getBoundaryPath(boundaryOrElement)
}

/** Returns true if the node belongs to the same or a nested boundary as the parent.

 @example
 const isTheSameBoundary = isInBoundary(parentNode)
 ...
 if (isTheSameBoundary(event.target)) { ... }

 */
export const isInBoundary = (parent: PathOrElement) => {
  const parentPath = resolveBoundaryPath(parent)
  return (child: PathOrElement) => {
    const childPath = resolveBoundaryPath(child)
    if (childPath.length < parentPath.length) return false
    return childPath.indexOf(parentPath) === 0
  }
}

// @deprecated use isInBoundary(parent)(child)
export function isNodeInBoundary(
  node: Element,
  boundaries: Array<string>
): boolean {
  const parentPath = getBoundaryPath(boundaries)
  const nodePath = getNodeBoundaryPath(node)
  return isInBoundary(parentPath)(nodePath)
}

export const boundaryContextTypes = {
  boundaries: PropTypes.array,
}

const BOUNDARY_ID = Symbol('boundaryId')

export function boundaryDecorator(
  options: {
    boundaryName?: string
  } = {}
) {
  return (Component: {new (...args: any): any}) =>
    class ComponentWithBoundary extends Component {
      static propTypes = {
        /* Name of this boundary, will be included in the id */
        boundaryName: PropTypes.string,
        /* Resets parent boundaries */
        parentBoundaries: PropTypes.array,
        // @ts-ignore Ignored during migration
        ...Component.propTypes,
      }

      static contextTypes = {
        ...boundaryContextTypes,
        // @ts-ignore Ignored during migration
        ...Component.contextTypes,
      }

      static childContextTypes = {
        ...boundaryContextTypes,
        // @ts-ignore Ignored during migration
        ...Component.childContextTypes,
      }

      getChildContext() {
        return {
          ...(super.getChildContext ? super.getChildContext() : undefined),
          boundaries: this.boundaries,
        }
      }

      get parentBoundaries(): Array<string> {
        const {parentBoundaries} = this.props
        return parentBoundaries || this.context.boundaries || []
      }

      get boundaryId(): string | undefined | null {
        const {boundaryName = options.boundaryName} = this.props
        if (!boundaryName) return undefined

        // @ts-ignore Ignored during migration
        const id = this[BOUNDARY_ID] || (this[BOUNDARY_ID] = _.uniqueId())
        return `${id}:${boundaryName}`
      }

      get boundaries(): Array<string> {
        const boundaryId = this.boundaryId
        if (boundaryId) return [...this.parentBoundaries, boundaryId]
        return this.parentBoundaries
      }

      get boundaryProps(): any {
        return {[BOUNDARY_ATTR]: getBoundaryPath(this.boundaries)}
      }
    }
}

// @ts-ignore Ignored during migration
@boundaryDecorator()
class Boundary extends React.Component<
  {
    children?: Function | React.ReactNode
    boundaryName?: string
  } & any
> {
  static displayName = 'Boundary'

  // mixed-in by @boundaryDecorator
  boundaryProps: any

  render() {
    const child = this.props.children
    if (child == null) return null
    return cloneCreateOrCallNode(
      child,
      mergeProps(
        {},
        // @ts-ignore Ignored during migration
        typeof child === 'object' && child.props,
        forwardProps(this),
        this.boundaryProps
      )
    )
  }
}

/* Exposes current boundaries array to children.

  @example

  <Boundary>
    <div>
      <WithBoundaries>{
        ({boundaries}) =>
          ... do something with the boundaries
      }</WithBoundaries>
  </Boundary>

*/
export class WithBoundaries extends React.Component<{
  children: Function
}> {
  static displayName = 'WithBoundaries'

  static propTypes = {
    children: PropTypes.func.isRequired,
  }

  static contextTypes = {
    ...boundaryContextTypes,
  }

  render() {
    const {children, ...props} = this.props
    // @ts-ignore Ignored during migration
    return children({
      ...props,
      boundaries: this.context.boundaries || [],
    })
  }
}

export {Boundary}
export default Boundary
